/**
 * Remix button visibility for Malleable Content. Canonical deny list and show rules.
 * Use with block-controls activeNode (or a full PM node) and these filters in both edit and view.
 */

import { isNodeEmpty } from '@atlaskit/editor-common/utils';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	isAllowedMBEExtensionForRemix,
	isRemixRestrictedNodeType,
} from '@atlassian/malleable-ui-components/remix/remix-config';

/** Node types that get the Ask Rovo button on their floating toolbar (media nodes). */
export const ASK_ROVO_MEDIA_NODE_TYPES: readonly string[] = ['mediaSingle', 'media'] as const;

export const isConsideredEmptyForRemix = (node?: PMNode): boolean => {
	if (!node) {
		return false;
	}
	// Aya's principle:
	// Empty anything should not have the Remix button shown
	// Once there is text or something in the element, we can show it
	// NOTE: Currently, we are ignoring "something" because Remix can't handle only emoji, status, etc.
	if (
		node.textContent.trim().length === 0 &&
		fg('platform_editor_remix_improved_emptiness_check')
	) {
		return true;
	}
	// layoutSection is always remixable (even empty) so users can generate content into it.
	return node.type.name !== 'layoutSection' && isNodeEmpty(node);
};

/**
 * MBE extension keys that are allowed to use Remix in Confluence.
 * The allow list is owned by the product layer so product-specific
 * decisions stay out of platform code.
 *
 * Already gated by the existing `cc-maui` experiment (controls Remix
 * visibility) and `confluence_frontend_native_tabs_extension` (controls
 * whether native tabs nodes exist in the editor).
 */
const REMIX_MBE_ALLOW_LIST: ReadonlyArray<string> = ['native-tabs'];

/**
 * Reusable filter for remix button visibility. Use with block-controls activeNode
 * (nodeType / rootNodeType) or a full PM node in both edit and view.
 *
 * - Deny-list is always applied when we have a type (never show Remix on media, extensions, etc.).
 * - multiBodiedExtension nodes are an exception: they are allowed when their extensionKey is in
 *   the MBE allow list (e.g. native tabs), so Remix is scoped to specific MBE variants only.
 * - When a full node is provided, we also require the node to be non-empty (no Remix on empty blocks).
 * - When only nodeType is available (e.g. from activeNode in view mode), we only apply the deny-list.
 */
export const shouldShowRemixForSelectionContext = (context: {
	node?: PMNode;
	nodeType?: string;
}): boolean => {
	const nodeType = context.node?.type?.name ?? context.nodeType;
	if (nodeType === undefined) {
		return false;
	}
	// Allow specific MBE variants (e.g. native tabs) even though the generic
	// multiBodiedExtension type is in the deny list.
	if (nodeType === 'multiBodiedExtension' && context.node) {
		const extensionKey = context.node.attrs?.extensionKey;
		if (isAllowedMBEExtensionForRemix(extensionKey, REMIX_MBE_ALLOW_LIST)) {
			return !isConsideredEmptyForRemix(context.node);
		}
		return false;
	}
	if (isRemixRestrictedNodeType(nodeType)) {
		return false;
	}
	return !isConsideredEmptyForRemix(context.node);
};
